import React, { useContext, useEffect, useState } from 'react';
import { View } from 'react-native';
import { useMutation } from '@apollo/client';
import styled from 'styled-components/native';

import DataState from 'app/src/components/DataState';
import AuthContext from 'app/src/contexts/AuthContext';
import { MainContainer, ContentContainer, PageTitle } from 'app/src/elements';
import { Button } from 'app/src/elements/buttons';
import { StyledTextInput, StyledPicker } from 'app/src/elements/inputs';
import { Picker } from 'app/src/elements/Picker';
import { Text } from 'app/src/styles';

import { UPDATE_FEE_MARKUP } from './queries';

const Label = styled(Text)`
  width: 100px;
  font-weight: 600;
`;

const HeaderRow = styled(View)`
  flex-direction: row;
  margin-top: 12px;
`;

const HeaderColumn = styled(View)`
  width: 100px;
`;

const Header = styled(Text)`
  font-weight: 600;
`;

const formatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  minimumFractionDigits: 2,
});

export const formatAmount = amount => {
  if (!amount) { return ''; }
  return formatter.format(amount);
};

const FeeType = ({ feeType, feeMarkups, seller, refetch }) => {
  const feeMarkup = feeMarkups.find(entry => entry.feeType === feeType);

  const [markupType, setMarkupType] =
    useState(feeMarkup?.markupType || 'percent');

  const [amount, setAmount] = useState(formatAmount(feeMarkup?.amount));

  const [updateFeeMarkup] = useMutation(UPDATE_FEE_MARKUP, {
    onCompleted: () => {
      if (refetch) { refetch(); }
    }
  });

  const save = () => {
    updateFeeMarkup({
      variables: {
        sellerId: seller?.id, feeType, markupType, amount
      }
    });
  }

  const canSave = amount && (
    parseFloat(feeMarkup?.amount) !== parseFloat(amount) ||
    feeMarkup?.markupType !== markupType
  );

  return (
    <View style={{ flexDirection: 'row', marginBottom: 4 }}>
      <Label style={{ width: 200 }}>
        {feeType}{(seller && feeMarkup?.defaultMarkup) ? ' *' : ''}
      </Label>

      <StyledPicker
        style={{ width: 120 }}
        selectedValue={markupType}
        onChange={e => setMarkupType(e.target.value)}
      >
        <Picker.Item value="percent" label="Percent" />
        <Picker.Item value="flat" label="Flat $" />
      </StyledPicker>

      <StyledTextInput
        style={{ width: 100, textAlign: 'right' }}
        onChange={e => setAmount(e.target.value)}
        value={amount}
      />

      <View style={{ width: 20 }}>
        {'percent' === markupType ? <Text>%</Text> : null}
      </View>

      <Button
        style={{ marginTop: 0, marginBottom: 0, width: 80 }}
        label="Save"
        onPress={save}
        disabled={!canSave}
      />
    </View>
  );
};

const FeeMarkups = ({ seller, sellerFeeMarkups, refetch }) => {
  const { feeTypes, settings, refetchData } = useContext(AuthContext);
  const [feeMarkups, setFeeMarkups] = useState(sellerFeeMarkups);

  useEffect(() => {
    if (settings && !sellerFeeMarkups) { setFeeMarkups(settings.feeMarkups); }
  }, [settings]);

  if (!feeMarkups) { return <DataState.Loading /> };

  const markupFeeTypes = feeTypes.filter(feeType => feeType.markup);

  return (
    <MainContainer>
      <ContentContainer>
        <PageTitle>Default Fee Markups</PageTitle>
        {seller && (
          <Text style={{ marginBottom: 6 }}>(* = uses system default)</Text>
        )}

        <HeaderRow>
          <HeaderColumn style={{ width: 200 }}>
          </HeaderColumn>

          <HeaderColumn style={{ width: 120 }}>
            <Header>Markup Type</Header>
          </HeaderColumn>

          <HeaderColumn style={{ width: 120 }}>
            <Header>Amount</Header>
          </HeaderColumn>
        </HeaderRow>

        {markupFeeTypes.map(feeType => (
          <FeeType
            key={feeType.name}
            feeType={feeType.name}
            feeMarkups={feeMarkups}
            seller={seller}
            refetch={() => {
              if (!refetch) { refetchData(); }
            }}
          />
        ))}
      </ContentContainer>
    </MainContainer>
  )
};

export default FeeMarkups;
